/* V153 — Hook for one-click video recording of the ad stage.
   V156.1 — Batch-friendly: separates session (screen-capture stream) from
            per-segment recording, so the user grants permission ONCE and we
            can chain 18 recordings automatically without re-prompting. */

import { useState, useCallback, useRef } from "react";

const DURATION_MS = 44_000; // one full loop

export function useAdRecorder() {
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const tickRef = useRef(null);

  // Fully end the session — stops the stream so the browser stops sharing
  const endSession = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    setRecording(false);
    setProgress(0);
  }, []);

  // Stop just the current recorder but KEEP the stream alive for next segment
  const stopSegment = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
  }, []);

  // Backward-compatible "stop" — fully ends session like before
  const stop = endSession;

  // Acquire the screen-capture stream once (requires user gesture).
  // Returns true on success.
  const ensureStream = useCallback(async () => {
    if (streamRef.current && streamRef.current.active) return true;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setError("Tvoj prehliadač nepodporuje Screen Capture API. Použi Chrome / Firefox / Edge / Safari 13+.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30, max: 60 } },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: "include",
      });
      streamRef.current = stream;
      stream.getVideoTracks()[0].onended = () => endSession();
      return true;
    } catch (e) {
      if (e?.name !== "NotAllowedError") {
        setError("Nepodarilo sa spustiť nahrávanie: " + (e?.message || e));
      }
      return false;
    }
  }, [endSession]);

  const start = useCallback(
    async ({ filenameBase = "toprankpulse-ad", durationMs = DURATION_MS } = {}) => {
      setError(null);
      const ok = await ensureStream();
      if (!ok) return;
      const stream = streamRef.current;

      const candidates = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
        "video/mp4",
      ];
      const mimeType = candidates.find((c) => MediaRecorder.isTypeSupported(c)) || "video/webm";

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 5_000_000,
      });
      recorderRef.current = recorder;

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const ext = mimeType.includes("mp4") ? "mp4" : "webm";
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filenameBase}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        setRecording(false);
        setProgress(0);
        // NOTE: stream is intentionally NOT stopped here — batch mode reuses it.
      };

      recorder.start();
      setRecording(true);
      setProgress(0);

      const startTs = Date.now();
      tickRef.current = setInterval(() => {
        const pct = Math.min(100, ((Date.now() - startTs) / durationMs) * 100);
        setProgress(pct);
      }, 200);

      timerRef.current = setTimeout(() => {
        stopSegment();
      }, durationMs);
    },
    [ensureStream, stopSegment]
  );

  return { recording, progress, error, start, stop, endSession };
}
